import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Grid, InputAdornment, Typography } from "@mui/material";
import moment from "moment";

import InputFieldText from "Components/Common/InputFieldText";
import DateField from "Components/Common/DatePicker";
import { getOptionValue } from "common/enums";
import { applicationTypes } from "common/constant";

import { onlyNumberKeys } from "utils";

import { usePremiseTypeQuery } from "apis/PremiseType";

const fourMonthsAfterDate = moment().add(5, "months");

const UtilitiesFields = ({ formik, t }) => {
  const {
    setFieldValue,
    values: {
      utilities,
      expectedConnectionEndDate,
      expectedConnectionStartDate,
      applicationTypeId,
      utilityAddress: { premiseTypeId, dcsDomesticPremiseType },
    },
  } = formik;

  const { data: { data: premiseTypes } = {} } = usePremiseTypeQuery();

  const isNonDomesticPremise = useMemo(
    () =>
      premiseTypes
        ?.find((premiseType) => premiseType.id === premiseTypeId)
        ?.name.toLowerCase() === "non-domestic",
    [premiseTypes, premiseTypeId]
  );

  const isDcsConnectionForIndividualUnit = useMemo(
    () =>
      dcsDomesticPremiseType ===
      getOptionValue("dcsConnectionTypeEnum", "Individual Units"),
    [dcsDomesticPremiseType]
  );

  const { isDcsUtility, isWaterSewageSolidWasteUtility } = useMemo(
    () => ({
      isDcsUtility: utilities.some(
        ({ value }) => value === getOptionValue("utilityOptions", "DCS")
      ),
      isWaterSewageSolidWasteUtility: utilities.some(
        ({ value }) =>
          value === getOptionValue("utilityOptions", "Water") ||
          value === getOptionValue("utilityOptions", "Sewage") ||
          value === getOptionValue("utilityOptions", "Solid Waste")
      ),
    }),
    [utilities]
  );

  const minStartDate = useMemo(
    () =>
      isDcsUtility || (isDcsUtility && isWaterSewageSolidWasteUtility)
        ? fourMonthsAfterDate
        : moment(),
    [isDcsUtility, isWaterSewageSolidWasteUtility]
  );

  useEffect(() => {}, [
    isDcsUtility,
    isWaterSewageSolidWasteUtility,
    expectedConnectionStartDate,
    expectedConnectionEndDate,
  ]);

  useEffect(() => {
    setFieldValue("extraValues.isNonDomesticPremise", isNonDomesticPremise);
    if (isNonDomesticPremise) {
      setFieldValue("dcsDemand.waterFlowRate", "");
      setFieldValue("dcsDemand.chilledWaterLineDiameter", "");
    }
  }, [isNonDomesticPremise]);

  useEffect(() => {
    if (
      !utilities.some(
        ({ value }) => value === getOptionValue("utilityOptions", "DCS")
      )
    ) {
      setFieldValue("dcsDemand.contractDemand", "");
      setFieldValue("dcsDemand.ultimateDemand", "");
      setFieldValue("dcsDemand.waterFlowRate", "");
      setFieldValue("dcsDemand.chilledWaterLineDiameter", "");
    }

    if (
      !utilities.some(
        ({ value }) =>
          value === getOptionValue("utilityOptions", "Water") ||
          value === getOptionValue("utilityOptions", "Sewage")
      )
    ) {
      setFieldValue("waterSewageDemand.waterDemand", "");
      setFieldValue("waterSewageDemand.flushing", "");
      setFieldValue("waterSewageDemand.domestic", "");
      setFieldValue("waterSewageDemand.gardening", "");
    }
  }, [utilities]);

  return (
    <Grid container columnSpacing={1} rowSpacing={1}>
      <Grid item xs={12} sm={6} lg={4}>
        <InputFieldText
          label={t("Total Build Up Area") + "*"}
          name="totalBuildupArea"
          formik={formik}
          type="number"
          placeholder={t("Enter Total Build Up Area")}
          InputProps={{
            endAdornment: <InputAdornment position="end">sq m</InputAdornment>,
            inputProps: { min: 0, onKeyDown: (e) => onlyNumberKeys(e, true) },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={4}>
        <InputFieldText
          label={t("Total Number of Occupants") + "*"}
          name="totalNumberOfOccupants"
          formik={formik}
          type="number"
          placeholder={t("Enter Total Number of Occupants")}
        />
      </Grid>
      {applicationTypeId === applicationTypes["Temporary Connection"] && (
        <>
          <Grid item xs={12} sm={6} lg={4}>
            <DateField
              label={t("Connection Start Date") + "*"}
              name="expectedConnectionStartDate"
              formik={formik}
              views={["month", "year"]}
              minDate={minStartDate}
              maxDate={expectedConnectionEndDate ?? ""}
              placeholder="Select Connection Start Date"
              disablePast
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <DateField
              label={t("Connection End Date") + "*"}
              name="expectedConnectionEndDate"
              formik={formik}
              views={["month", "year"]}
              minDate={
                expectedConnectionStartDate
                  ? moment(expectedConnectionStartDate).add(1, "months")
                  : minStartDate
              }
              placeholder="Select Connection End Date"
              disablePast
            />
          </Grid>
        </>
      )}
      {utilities.some(
        ({ value }) => value === getOptionValue("utilityOptions", "DCS")
      ) && (
        <>
          <Grid item xs={12}>
            <Typography className="mb-3" lineHeight="1" variant="h6">
              {t("DCS Demand Details")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <InputFieldText
              label={
                t(isNonDomesticPremise ? "Contract Demand" : "Demand Load") +
                "*"
              }
              name="dcsDemand.contractDemand"
              formik={formik}
              type="number"
              placeholder={t("Enter Contract Demand")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">TR</InputAdornment>
                ),
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <InputFieldText
              label={t("Ultimate Demand") + "*"}
              name="dcsDemand.ultimateDemand"
              formik={formik}
              type="number"
              placeholder={t("Enter Ultimate Demand")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">TR</InputAdornment>
                ),
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
          {isDcsConnectionForIndividualUnit && (
            <>
              <Grid item xs={12}>
                <Typography className="mb-3" lineHeight="1" variant="h6">
                  {t(
                    "Meter Requisition Details (To be filled only if new meter is required)"
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <InputFieldText
                  label={t("Chilled Water Line Diameter") + "*"}
                  name="dcsDemand.chilledWaterLineDiameter"
                  formik={formik}
                  type="number"
                  placeholder="Enter Chilled Water Line Diameter"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">MM</InputAdornment>
                    ),
                    inputProps: {
                      min: 0,
                      onKeyDown: (e) => onlyNumberKeys(e, true),
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <InputFieldText
                  label={t("Water Flow Rate") + "*"}
                  name="dcsDemand.waterFlowRate"
                  formik={formik}
                  type="number"
                  placeholder="Enter water Flow Rate"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        Liters/Second
                      </InputAdornment>
                    ),
                    inputProps: {
                      min: 0,
                      onKeyDown: (e) => onlyNumberKeys(e, true),
                    },
                  }}
                />
              </Grid>
            </>
          )}
        </>
      )}

      {utilities.some(
        ({ value }) =>
          value === getOptionValue("utilityOptions", "Water") ||
          value === getOptionValue("utilityOptions", "Sewage")
      ) && (
        <>
          <Grid item xs={12}>
            <Typography className="mb-3" lineHeight="1" variant="h6">
              {t("Water / Sewage Demand Details")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <InputFieldText
              label={t("Total Water Demand") + "*"}
              name="waterSewageDemand.waterDemand"
              formik={formik}
              type="number"
              placeholder={t("Enter Total Water Demand")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">Liters/day</InputAdornment>
                ),
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <InputFieldText
              label={t("Flushing") + "*"}
              name="waterSewageDemand.flushing"
              formik={formik}
              type="number"
              placeholder="Enter Flushing"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">Liters/day</InputAdornment>
                ),
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <InputFieldText
              label={t("Domestic") + "*"}
              name="waterSewageDemand.domestic"
              formik={formik}
              type="number"
              placeholder="Enter Domestic"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">Liters/day</InputAdornment>
                ),
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <InputFieldText
              label={t("Gardening") + "*"}
              name="waterSewageDemand.gardening"
              formik={formik}
              type="number"
              placeholder="Enter Gardening"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">Liters/day</InputAdornment>
                ),
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

UtilitiesFields.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(UtilitiesFields);
